// src/pages/AdmissionNote.jsx

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Save, ArrowLeft, CheckCircle, Sparkles, Copy } from 'lucide-react';
import { motion } from 'framer-motion';
import SmartCaptureModal from '../components/SmartCaptureModal';
import { apiPost } from '../utils/api'; // ✅ centralized backend helper

// --- Reusable Text Area for Note Sections ---
const NoteSectionTextarea = ({ label, placeholder, ...props }) => (
  <div>
    <label className="block text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">{label}</label>
    <textarea
      placeholder={placeholder}
      {...props}
      className="w-full h-48 bg-slate-100 dark:bg-zinc-800 border-none rounded-lg p-4 text-base text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
    />
  </div>
);

export default function AdmissionNote() {
  const { patientId, noteId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [showSmartCapture, setShowSmartCapture] = useState(false);
  const [noteData, setNoteData] = useState({
    hpi: '',
    ros: '',
    exam: '',
    ap: '',
    mdm: '',
    pastMedicalHistory: '',
    medications: '',
    allergies: '',
    pastSurgicalHistory: '',
    socialHistory: '',
    familyHistory: '',
  });

  // Patient context (used by Fix HPI)
  const [patientName, setPatientName] = useState('');
  const [patientSex, setPatientSex] = useState('');
  const [patientAge, setPatientAge] = useState(null);

  const [activeTab, setActiveTab] = useState('HPI');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isGenerating, setIsGenerating] = useState(null);
  const [isFixing, setIsFixing] = useState(null); // 'hpi' | 'meds' | null
  const [errorMsg, setErrorMsg] = useState('');

  const noteTabs = ['HPI', 'History', 'ROS', 'Exam', 'A&P', 'MDM'];

  const fetchPatientForContext = useCallback(async () => {
    if (!patientId) return;
    const patientRef = doc(db, 'patients', patientId);
    const snap = await getDoc(patientRef);
    if (snap.exists()) {
      const d = snap.data();
      const full = `${d.firstName || ''} ${d.lastName || ''}`.trim();
      setPatientName(full);
      if (typeof d.age === 'number') setPatientAge(d.age);
      if (typeof d.sex === 'string') setPatientSex(d.sex);
    }
  }, [patientId]);

  useEffect(() => {
    const initializeNote = async () => {
      setErrorMsg('');
      await fetchPatientForContext();

      if (noteId && noteId !== 'new') {
        const noteRef = doc(db, 'notes', noteId);
        const noteSnap = await getDoc(noteRef);
        if (noteSnap.exists()) {
          setNoteData(prev => ({ ...prev, ...noteSnap.data() }));
        }
      } else if (noteId === 'new') {
        setNoteData({
          hpi: '## Admission H&P\n\n**History of Present Illness:**\n\n',
          ros: '',
          exam: '',
          ap: '',
          mdm: '',
          pastMedicalHistory: '',
          medications: '',
          allergies: '',
          pastSurgicalHistory: '',
          socialHistory: '',
          familyHistory: '',
        });
      }
    };
    initializeNote();
  }, [noteId, patientId, fetchPatientForContext]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNoteData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setIsSaved(false);
    setErrorMsg('');

    const summary =
      `HPI:\n${noteData.hpi}\n\n` +
      `ROS:\n${noteData.ros}\n\n` +
      `Exam:\n${noteData.exam}\n\n` +
      `A&P:\n${noteData.ap}\n\n` +
      `MDM:\n${noteData.mdm}`;

    const payload = { ...noteData, summary, timestamp: serverTimestamp() };

    try {
      if (noteId && noteId !== 'new') {
        const noteRef = doc(db, 'notes', noteId);
        await setDoc(noteRef, payload, { merge: true });
      } else {
        const newNoteRef = await addDoc(collection(db, 'notes'), {
          ...payload,
          ownerId: currentUser.uid,
          patientID: patientId,
          type: 'Admission',
        });
        navigate(`/admission-note/${patientId}/${newNoteRef.id}`, { replace: true });
      }
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 1800);
    } catch (e) {
      setErrorMsg(e?.message || 'Failed to save note.');
    } finally {
      setIsSaving(false);
    }
  };

  // --- AI: Generate AP / MDM (via apiPost helper) ---
  const handleGenerate = async (type) => {
    setIsGenerating(type);
    setErrorMsg('');

    try {
      if (type === 'ap') {
        const current_note_context =
          `HPI:\n${noteData.hpi}\n\nROS:\n${noteData.ros}\n\nExam:\n${noteData.exam}`;

        const payload = {
          context: 'admission',
          current_note_context,
        };

        const result = await apiPost('/generate-ap-v2', payload); // → /v1/generate-ap-v2
        setNoteData(prev => ({ ...prev, ap: result.summary || '' }));
      }

      if (type === 'mdm') {
        const payload = {
          hpi: noteData.hpi || '',
          exam: noteData.exam || '',
          objective: noteData.ros || '',
          assessment: noteData.ap || '',
        };

        const result = await apiPost('/generate-mdm', payload); // → /v1/generate-mdm
        setNoteData(prev => ({ ...prev, mdm: result.summary || '' }));
      }
    } catch (err) {
      const msg = err?.message || 'AI generation failed.';
      setErrorMsg(msg);
      if (type === 'ap') {
        setNoteData(prev => ({ ...prev, ap: `Error: ${msg}` }));
      }
      if (type === 'mdm') {
        setNoteData(prev => ({ ...prev, mdm: `Error: ${msg}` }));
      }
    } finally {
      setIsGenerating(null);
    }
  };

  // --- AI: Fix HPI (via /v1/format-hpi) ---
  const handleFixHpi = async () => {
    setIsFixing('hpi');
    setErrorMsg('');

    try {
      const body = {
        hpi: noteData.hpi || '',
        pmh: noteData.pastMedicalHistory || '',
        ros: noteData.ros || '',
        patient_name: patientName || null,
        sex: patientSex || null,
        age: typeof patientAge === 'number' ? patientAge : null,
      };

      const result = await apiPost('/format-hpi', body); // → /v1/format-hpi
      setNoteData(prev => ({ ...prev, hpi: result.formatted_hpi || prev.hpi }));
    } catch (err) {
      setErrorMsg(err?.message || 'Fix HPI failed.');
    } finally {
      setIsFixing(null);
    }
  };

  // --- AI: Fix Meds (via /v1/normalize-medications) ---
  const handleFixMeds = async () => {
    setIsFixing('meds');
    setErrorMsg('');

    try {
      const result = await apiPost('/normalize-medications', {
        text: noteData.medications || '',
      }); // → /v1/normalize-medications

      setNoteData(prev => ({
        ...prev,
        medications: result.summary || prev.medications,
      }));
    } catch (err) {
      setErrorMsg(err?.message || 'Fix Meds failed.');
    } finally {
      setIsFixing(null);
    }
  };

  const handleCopy = () => {
    const { hpi, ros, exam, ap, mdm } = noteData;
    const fullText = `HPI:\n${hpi}\n\nROS:\n${ros}\n\nExam:\n${exam}\n\nA&P:\n${ap}\n\nMDM:\n${mdm}`;
    navigator.clipboard.writeText(fullText);
    alert('Note copied to clipboard!');
  };

  return (
    <div className="bg-[#f4f7f9] dark:bg-black min-h-screen">
      <Header />
      <main className="max-w-5xl mx-auto py-10 px-4">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl">
          <div className="p-6 border-b border-gray-200 dark:border-zinc-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-800"
                >
                  <ArrowLeft size={20} />
                </button>
                <div>
                  <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                    Admission Note
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400">
                    for {patientName || 'patient'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowSmartCapture(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full font-semibold hover:bg-purple-600 transition"
                >
                  <Sparkles size={16} /> Smart Capture
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving || isSaved}
                  className={`flex items-center justify-center gap-2 px-8 py-2 rounded-full font-semibold text-white transition-all duration-300 ${
                    isSaved ? 'bg-green-500' : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                >
                  {isSaving ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : isSaved ? (
                    <>
                      <CheckCircle size={18} /> Saved!
                    </>
                  ) : (
                    <>
                      <Save size={18} /> Save Note
                    </>
                  )}
                </button>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-full font-semibold hover:bg-slate-700 transition"
                >
                  <Copy size={16} /> Copy
                </button>
              </div>
            </div>
            {!!errorMsg && (
              <div className="mt-3 text-sm text-red-600 dark:text-red-400">{errorMsg}</div>
            )}
          </div>

          <div className="p-6">
            <div className="flex justify-center mb-8">
              <div className="flex items-center space-x-2 bg-slate-200/70 dark:bg-zinc-800 rounded-full p-1">
                {noteTabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className="relative px-5 py-1.5 text-sm font-semibold transition-colors duration-300"
                  >
                    <span
                      className={`relative z-10 ${
                        activeTab === tab
                          ? 'text-gray-800 dark:text-white'
                          : 'text-gray-500 hover:text-gray-800 dark:hover:text-white'
                      }`}
                    >
                      {tab}
                    </span>
                    {activeTab === tab && (
                      <motion.div
                        layoutId="active-note-pill"
                        className="absolute inset-0 bg-white dark:bg-zinc-700 rounded-full shadow-sm"
                        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* HPI */}
            {activeTab === 'HPI' && (
              <div className="space-y-3">
                <NoteSectionTextarea
                  label="History of Present Illness"
                  name="hpi"
                  value={noteData.hpi}
                  onChange={handleChange}
                />
                <div className="flex justify-end">
                  <button
                    onClick={handleFixHpi}
                    disabled={isFixing === 'hpi'}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-full font-semibold hover:bg-indigo-700 transition"
                  >
                    {isFixing === 'hpi' ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>✨ Fix HPI</>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* History */}
            {activeTab === 'History' && (
              <div className="space-y-4">
                <NoteSectionTextarea
                  label="Past Medical History"
                  name="pastMedicalHistory"
                  value={noteData.pastMedicalHistory}
                  onChange={handleChange}
                />
                <div>
                  <NoteSectionTextarea
                    label="Medications"
                    name="medications"
                    value={noteData.medications}
                    onChange={handleChange}
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={handleFixMeds}
                      disabled={isFixing === 'meds'}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-full font-semibold hover:bg-emerald-700 transition"
                    >
                      {isFixing === 'meds' ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>💊 Fix Meds</>
                      )}
                    </button>
                  </div>
                </div>
                <NoteSectionTextarea
                  label="Allergies"
                  name="allergies"
                  value={noteData.allergies}
                  onChange={handleChange}
                />
                <NoteSectionTextarea
                  label="Past Surgical History"
                  name="pastSurgicalHistory"
                  value={noteData.pastSurgicalHistory}
                  onChange={handleChange}
                />
                <NoteSectionTextarea
                  label="Social History"
                  name="socialHistory"
                  value={noteData.socialHistory}
                  onChange={handleChange}
                />
                <NoteSectionTextarea
                  label="Family History"
                  name="familyHistory"
                  value={noteData.familyHistory}
                  onChange={handleChange}
                />
              </div>
            )}

            {/* ROS */}
            {activeTab === 'ROS' && (
              <NoteSectionTextarea
                label="Review of Systems"
                name="ros"
                value={noteData.ros}
                onChange={handleChange}
              />
            )}

            {/* Exam */}
            {activeTab === 'Exam' && (
              <NoteSectionTextarea
                label="Physical Exam"
                name="exam"
                value={noteData.exam}
                onChange={handleChange}
              />
            )}

            {/* A&P */}
            {activeTab === 'A&P' && (
              <div className="space-y-4">
                <NoteSectionTextarea
                  label="Assessment & Plan"
                  name="ap"
                  value={noteData.ap}
                  onChange={handleChange}
                />
                <button
                  onClick={() => handleGenerate('ap')}
                  disabled={isGenerating}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full font-semibold hover:bg-purple-600 transition"
                >
                  {isGenerating === 'ap' ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Sparkles size={16} /> Generate A&P
                    </>
                  )}
                </button>
              </div>
            )}

            {/* MDM */}
            {activeTab === 'MDM' && (
              <div className="space-y-4">
                <NoteSectionTextarea
                  label="Medical Decision Making"
                  name="mdm"
                  value={noteData.mdm}
                  onChange={handleChange}
                />
                <button
                  onClick={() => handleGenerate('mdm')}
                  disabled={isGenerating}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full font-semibold hover:bg-purple-600 transition"
                >
                  {isGenerating === 'mdm' ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Sparkles size={16} /> Generate MDM
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      <SmartCaptureModal
        show={showSmartCapture}
        onClose={() => setShowSmartCapture(false)}
        onSubmit={(structuredData) => {
          setNoteData(prev => ({
            ...prev,
            hpi: structuredData.hpi || prev.hpi,
            pastMedicalHistory: structuredData.past_medical_history || prev.pastMedicalHistory,
            medications: structuredData.medications || prev.medications,
            allergies: structuredData.allergies || prev.allergies,
            pastSurgicalHistory: structuredData.past_surgical_history || prev.pastSurgicalHistory,
            socialHistory: structuredData.social_history || prev.socialHistory,
            familyHistory: structuredData.family_history || prev.familyHistory,
            ros: structuredData.review_of_systems || prev.ros,
          }));
        }}
      />
    </div>
  );
}
